import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../store/store";
import ButtonLoader from "../../../atoms/ButtonLoader/ButtonLoader";
import QuestionField from "../CoachAssessment/QuestionField";
import {
  AudienceBadge,
  sectionIcon,
} from "../CoachAssessment/sectionIcons";
import { computeSpanFullIds } from "../CoachAssessment/questionLayout";
import FieldEditHistory from "./FieldEditHistory";
import { useSharedProfile } from "./useSharedProfile";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";
import "../CoachAssessment/CoachAssessmentPage.scss";
import "./SharedProfilePage.scss";

const displayAudience = (section: any): "patient" | "coach" | "internal" => {
  if (section?.audience === "internal") return "internal";
  if (section?.audience === "patient") return "patient";
  return "coach";
};

const formatLogValue = (question: any, raw: string) => {
  if (!question || raw === "—" || raw === "") return raw;
  const options = question.options || [];
  if (!options.length) return raw;
  const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
  const mapped = parts.map((part) => {
    const hit = options.find(
      (o: any) =>
        String(o.id) === part ||
        String(o.label) === part ||
        String(o.emoji) === part,
    );
    return hit?.label || part;
  });
  return mapped.join(", ");
};

const SharedProfilePage: React.FC = () => {
  const params = useParams();
  const patientId =
    useSelector(
      (s: RootState) => s.patient.patientDetails?.data?.patient_id,
    ) ||
    params?.patientId ||
    undefined;
  const profile = useSharedProfile(patientId);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [activeChip, setActiveChip] = useState<string | null>(null);

  const sections = profile.sharedSections.filter((section: any) => {
    const questions = profile.questionsBySection[section.id] || [];
    return questions.length > 0 || profile.sectionSkipped(section);
  });
  const firstId = sections[0]?.id;
  const activeId = useMemo(
    () => activeChip || firstId || null,
    [activeChip, firstId],
  );

  if (profile.loading) {
    return (
      <div className="coach-assessment shared-profile">
        <PageHeader
          eyebrow="Shared across the care triad"
          title="Shared Patient Profile"
        />
        <div className="ca-empty">Loading shared profile…</div>
      </div>
    );
  }

  if (profile.error) {
    return (
      <div className="coach-assessment shared-profile">
        <PageHeader
          eyebrow="Shared across the care triad"
          title="Shared Patient Profile"
        />
        <div className="ca-empty">{profile.error}</div>
      </div>
    );
  }

  return (
    <div
      className={`coach-assessment shared-profile ${
        profile.editing ? "editing" : ""
      }`}
    >
      <PageHeader
        eyebrow="Shared across the care triad"
        title="Shared Patient Profile"
      />

      <div className="shared-editbar">
        <div className="seb-left">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
          </svg>
          <span>
            Edits update the record for all three coaches and are logged against
            your account.
          </span>
        </div>
        <div className="seb-actions">
          {profile.saveNote && !profile.editing && (
            <span className="seb-saved">{profile.saveNote}</span>
          )}
          {profile.editing ? (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={profile.saving}
                onClick={profile.cancelEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-dark"
                disabled={profile.saving}
                onClick={() => void profile.saveEdit()}
              >
                {profile.saving && <ButtonLoader />}
                {!profile.saving && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    <path d="M17 21v-8H7v8M7 3v5h8" />
                  </svg>
                )}
                {profile.saving ? "Saving…" : "Save changes"}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn btn-solid"
              onClick={profile.startEdit}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
              </svg>
              Edit profile
            </button>
          )}
        </div>
      </div>

      <div className="subnav">
        {sections.map((section: any) => {
          const skipped = profile.sectionSkipped(section);
          return (
            <button
              key={section.id}
              type="button"
              className={`subnav-chip ${activeId === section.id ? "is-on" : ""} ${
                skipped ? "is-skipped" : ""
              }`}
              onClick={() => {
                setActiveChip(section.id);
                setOpenSections((prev) => ({ ...prev, [section.id]: true }));
                document
                  .getElementById(`sec-${section.id}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
            >
              {section.name}
              {skipped ? (
                <span className="subnav-skip-dot" title="Skipped" />
              ) : null}
            </button>
          );
        })}
      </div>

      {sections.map((section: any) => {
        const questions = profile.questionsBySection[section.id] || [];
        const spanFull = computeSpanFullIds(questions);
        const { answered, total } = profile.sectionAnswered(section.id);
        const collapsed = openSections[section.id] === false;
        const aud = displayAudience(section);
        const skipped = profile.sectionSkipped(section);
        const sectionOptional =
          section.skippable === true ||
          (questions.length > 0 &&
            questions.every((q: any) => q.required !== true));
        return (
          <div
            key={section.id}
            className={`section ${collapsed ? "collapsed" : ""} ${
              skipped ? "is-skipped" : ""
            }`}
            id={`sec-${section.id}`}
            data-aud={aud}
          >
            <div
              className="section-head"
              onClick={() =>
                setOpenSections((prev) => ({
                  ...prev,
                  [section.id]: prev[section.id] === false,
                }))
              }
            >
              <div className="section-ico">
                {sectionIcon(section.id, section.icon)}
              </div>
              <div className="section-titles">
                <div className="section-title">{section.name}</div>
                <div className="section-meta">{section.blurb || ""}</div>
              </div>
              <div className="section-pills">
                {skipped ? (
                  <span
                    className="skip-pill"
                    title="Patient skipped this section"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      width="12"
                      height="12"
                      aria-hidden
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8 12h8" />
                    </svg>
                    Skipped
                  </span>
                ) : (
                  <span
                    className={`req-badge ${
                      sectionOptional ? "req-optional" : "req-required"
                    }`}
                  >
                    {sectionOptional ? "Optional" : "Required"}
                  </span>
                )}
                <AudienceBadge audience={aud} />
                <span
                  className={`count-pill ${
                    total > 0 && answered === total ? "done" : ""
                  }`}
                >
                  {answered}/{total} answered
                </span>
              </div>
              <svg
                className="chev"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            <div className="section-body">
              <div className="grid cols-2">
                {questions.map((question: any) => (
                  <div
                    key={question.id}
                    className={`field ${
                      spanFull.has(String(question.id)) ? "span-full" : ""
                    }`}
                  >
                    <div className="label">{question.prompt}</div>
                    <FieldEditHistory
                      label={question.prompt || question.id}
                      log={profile.sharedLog?.[question.id]}
                      formatValue={(v) => formatLogValue(question, v)}
                    />
                    {(question.helper || question.help) && (
                      <div className="helper">
                        {question.helper || question.help}
                      </div>
                    )}
                    <QuestionField
                      question={question}
                      value={profile.answers[question.id]}
                      disabled={!profile.editing}
                      answersOnly={!profile.editing}
                      onChange={(value) =>
                        profile.setAnswer(question.id, value)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SharedProfilePage;
